import { inject, Injectable } from '@angular/core';
import {
  faChartPie as fasChartPie,
  faClone as fasClone,
  faShapes as fasShapes,
  faWindowMaximize as fasWindowMaximize,
  IconDefinition,
} from '@fortawesome/pro-duotone-svg-icons';
import { EntitlementsService } from 'entitlements';
import { EnvService } from 'frontend-environment';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
} from 'rxjs';

export enum ProductId {
  Ads = 'ads',
  Email = 'email',
  Web = 'web',
  Data = 'data',
  Measure = 'measure',
}

export interface Product {
  id: ProductId | string;
  url: string;
  name: string;
  description: string;
  icon: IconDefinition;
  disabled: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private entitlementService = inject(EntitlementsService);
  private envService = inject<
    EnvService<{
      APP_DOMAIN: string;
      DATA_STUDIO_APP_URL: string;
      MEASUREMENT_STUDIO_APP_URL: string;
      ADEX_APP_URL: string;
      EMAIL_EXP_APP_URL: string;
      WEB_EXP_APP_URL: string;
      GOOGLE: string;
      MICROSOFT: string;
      PRIMARY: string;
      OKTA_ISSUER: string;
      IDP_DISCOVERY_REQUEST_CONTEXT: string;
      GOOGLE_IDP_ID: string;
      ZENDESK_IDP_URL: string;
      MICROSOFT_IDP_ID: string;
      OKTA_CLIENT_ID: string;
    }>
  >(EnvService);

  fasShapes = fasShapes;
  fasChartPie = fasChartPie;
  fasClone = fasClone;
  fasWindowMaximize = fasWindowMaximize;

  private productConfig = [
    {
      id: 'Central',
      url: '/central',
      name: 'Central',
      description:
        'The new and improved Central is here! Contact us to get access to your portal for self-service reporting and performance analytics.',
      icon: fasShapes,
      disabled: true,
      entitlementConfig: {
        requireHubReportsAccess: 'app.central',
        id: 'central',
      },
    },
    {
      id: ProductId.Data,
      url: '/data-studio',
      name: 'Data Studio',
      description:
        'Integrate, identify, and activate target accounts based on 1st and 3rd party data',
      icon: fasShapes,
      disabled: true,
      entitlementConfig: {
        requireNotLimitedView: true,
        id: 'data-studio',
      },
    },
    {
      id: ProductId.Measure,
      url: '/measure-studio',
      name: 'Measurement Studio',
      description:
        'Gain insight and understanding into marketing performance and attribution',
      icon: fasChartPie,
      disabled: true,
      entitlementConfig: {
        requireNotLimitedView: true,
        id: 'measure-studio',
      },
    },
    {
      id: ProductId.Ads,
      url: '/adex',
      name: 'Ad Experiences',
      description:
        'Run targeted digital ad campaigns across across multiple different ad networks',
      icon: fasClone,
      disabled: true,
      entitlementConfig: {
        id: 'adex',
        requireAdexMarketer: true,
      },
    },
    {
      id: ProductId.Web,
      url: this.envService.WEB_EXP_APP_URL,
      name: 'Web Experiences',
      description:
        'Turn any page on your website into a personalized landing page',
      icon: fasWindowMaximize,
      disabled: true,
      entitlementConfig: {
        requireNotLimitedView: true,
        id: 'web-experiences',
      },
    },
  ];

  public products$ = combineLatest(
    this.productConfig.map((product) =>
      this.entitlementService.getEntitlement$(product.entitlementConfig).pipe(
        map((entitlement) => ({
          ...product,
          // control defaulted to off: it flickers on for a moment for disabled customers
          disabled: entitlement !== true,
        })),
      ),
    ),
  ).pipe(
    distinctUntilChanged(),
    debounceTime(10),
    startWith(this.productConfig),
    shareReplay(1),
  );
}
